import { useCallback, useRef } from 'react';

import { ChartLoadingSkeleton } from '@/components/Charts/ChartLoadingSkeleton';
import { useLimitRate } from '@/components/TransactionModule/useLimitRate';
import { useTradeEngine } from '@/components/TransactionModule/useTradeEngine';

import { USD_STABLE_ADDRESSES_BY_CHAIN_ID } from '@/constants/addresses';
import { ADVANCED_ORDER_TYPE_LABELS, isTriggerOrderType } from '@/constants/advancedOrder';

import { useTokenResult } from '@/hooks/tokenQueries/useTokenResult';
import { useAdvancedOrderType } from '@/hooks/useAdvancedOrderType';
import { useFeature } from '@/hooks/useFeature';

import { Panel } from '@/ui/Panel';

import { TokenResult } from '@/utils/0x/token-registry.types';
import { classNames } from '@/utils/classnames';
import { ADVANCED_ORDER_NATIVE_CHART } from '@/utils/featureFlags';
import { ChainAddress } from '@/utils/models';

import { TradeLimitNativeChart } from './TradeLimitNativeChart';
import { TradeLimitTradingViewChart } from './TradeLimitTradingViewChart';

/** The order-price line shared by both chart implementations, in pair-rate units. */
export type LimitChartLine = {
  enabled: boolean;
  label: string;
  /** Primitive color key, resolvable as both `primitiveColors[color]` and `var(--color-${color})`. */
  color: 'matcha-green' | 'matcha-red';
  price: number | null;
  /** Referentially stable; receives the dragged pair rate. */
  onPriceChange: (price: number) => void;
};

type TradeLimitChartProps = {
  className?: string;
  // Whether the heavy TradingView widget may mount. Gated on the desktop media
  // query so the widget never mounts on mobile or before hydration; until then
  // the reserved slot shows a loading skeleton.
  canLoadWidget: boolean;
  buyToken: TokenResult | undefined;
  sellToken: TokenResult | undefined;

  isExpanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
};

export const TradeLimitChart = ({
  className,
  canLoadWidget,
  buyToken,
  sellToken,
  isExpanded,
  setIsExpanded,
}: TradeLimitChartProps) => {
  const isNativeChart = useFeature(ADVANCED_ORDER_NATIVE_CHART);

  return (
    <div
      className={classNames(
        'w-full flex flex-col gap-3 overflow-hidden',
        'transition-[max-width,opacity,transform] duration-300 ease-out',
        className,
      )}
    >
      <Panel className="w-full">
        {canLoadWidget ? (
          <LimitChartContent
            buyToken={buyToken}
            sellToken={sellToken}
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
            isNativeChart={isNativeChart}
          />
        ) : (
          <ChartLoadingSkeleton />
        )}
      </Panel>
    </div>
  );
};

type LimitChartContentProps = {
  buyToken: TokenResult | undefined;
  sellToken: TokenResult | undefined;
  isExpanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
  isNativeChart: boolean;
};

/**
 * Inner content rendered only while the chart is mounted. Shares the limit-rate
 * state with the form via `useLimitRate` (all state lives in the store, so
 * the two hook instances stay in sync) to draw the order-price line and write
 * the rate when the line is dragged.
 */
const LimitChartContent = ({
  buyToken,
  sellToken,
  isExpanded,
  setIsExpanded,
  isNativeChart,
}: LimitChartContentProps) => {
  const [orderType] = useAdvancedOrderType();
  const { tokenUsdPrices, hasUsdPriceResponse } = useTradeEngine();
  const singleSelectedToken =
    buyToken && !sellToken ? buyToken : sellToken && !buyToken ? sellToken : undefined;
  const { token: usdStableToken } = useTokenResult(
    singleSelectedToken
      ? (USD_STABLE_ADDRESSES_BY_CHAIN_ID[singleSelectedToken.chainId]?.values().next()
          .value as ChainAddress)
      : undefined,
    singleSelectedToken?.chainId,
  );

  const effectiveBuyToken = buyToken ?? (sellToken ? usdStableToken : undefined);
  const effectiveSellToken = sellToken ?? (buyToken ? usdStableToken : undefined);

  const { rate, side, setRate } = useLimitRate({
    sellToken: effectiveSellToken,
    buyToken: effectiveBuyToken,
    isLoading: false,
    tokenUsdPrices,
    hasUsdPriceResponse,
    autoInit: !isTriggerOrderType(orderType),
    orderType,
  });
  // The displayed side's rate is denominated to match the chart's y-axis for
  // the corresponding price view (sell -> pair-inverse, buy -> pair).
  const rateValue = rate[side];
  const limitPrice = rateValue ? Number(rateValue) : null;

  // Keep the latest drag inputs in a ref so the handler stays referentially
  // stable (the line subscription only resubscribes when the widget changes).
  const dragStateRef = useRef({ side });
  dragStateRef.current = { side };
  const handleLinePriceChange = useCallback(
    (price: number) => {
      const { side } = dragStateRef.current;
      // Pointer positions map to arbitrary floats; trim to chart-scale precision.
      setRate(String(Number(price.toPrecision(6))), side);
    },
    [setRate],
  );

  const line: LimitChartLine = {
    enabled: !singleSelectedToken,
    label: `${ADVANCED_ORDER_TYPE_LABELS[orderType]} Price`,
    color: orderType === 'stop-loss' ? 'matcha-red' : 'matcha-green',
    price: limitPrice,
    onPriceChange: handleLinePriceChange,
  };

  if (isNativeChart) {
    return (
      <TradeLimitNativeChart
        buyToken={effectiveBuyToken}
        sellToken={effectiveSellToken}
        side={side}
        line={line}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
      />
    );
  }

  return (
    <TradeLimitTradingViewChart
      buyToken={effectiveBuyToken}
      sellToken={effectiveSellToken}
      side={side}
      line={line}
      isExpanded={isExpanded}
      setIsExpanded={setIsExpanded}
    />
  );
};
